<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import { useI18n } from 'vue-i18n'
import { DTextField, DBtn, EyeIcon, EyeOffIcon } from 'front-library'

/**
 * store, router, notifications
 */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/**
 * Datos componente
 */
const showNewPassword = ref(true)
const showConfirmNewPassword = ref(true)
const loading = ref(false)
const wrongData = ref('')

/**
 * Datos formulario
 */
const dataChangePassword = ref({
  userNewPassword: '',
  userConfirmNewPassword: ''
})

/**
 * Esquema de validación campos obligatorios
 */
const validationSchema = object().shape({
  userNewPassword: string()
    .matches(
      /^(?=(?:.*\d))(?=.*[A-Z])(?=.*[a-z])(?=.*[;:.,_\-*!?¿¡/#$%&+|°"<>'])\S{8,64}$/g,
      t('auth.changeTemporaryPassword.form.valid.password')
    )
    .required(t('auth.changeTemporaryPassword.form.valid.required')),
  userConfirmNewPassword: string().when(
    'userNewPassword',
    (userNewPassword, schema) => {
      if (errors.value.userNewPassword === undefined) {
        return schema
          .equals(
            userNewPassword,
            t('auth.changeTemporaryPassword.form.valid.equalsPassword')
          )
          .required(t('auth.changeTemporaryPassword.form.valid.required'))
      } else {
        return schema
          .matches(
            /^(?=(?:.*\d))(?=.*[A-Z])(?=.*[a-z])(?=.*[;:.,_\-*!?¿¡/#$%&+|°"<>'])\S{8,64}$/g,
            t('auth.changeTemporaryPassword.form.valid.password')
          )
          .required(t('auth.changeTemporaryPassword.form.valid.required'))
      }
    }
  )
})

/**
 * Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario
 */
const { handleSubmit, errors } = useForm({
  initialValues: dataChangePassword.value,
  validationSchema
})

/**
 * Definicion de campos del formulario
 */
const { value: userNewPassword } = useField<string>('userNewPassword')
const { value: userConfirmNewPassword } = useField<string>(
  'userConfirmNewPassword'
)

/*
 * Metodo que valida los campos obligatorios del formulario
 */
const onSubmit = handleSubmit((values, actions) => {
  void changePassword(values)
})

/**
 * Metodo para cambiar la contraseña
 */
const changePassword = async (values: any) => {
  loading.value = true
  // Tomando los valores de email y pass del store
  values.userEmail = store.emailForgot
  values.userPassword = store.pass
  await store.passwordTemporary(values).then(result => {
    if (result === true) {
      store.emailForgot = ''
      store.pass = ''
      wrongData.value = ''
      //
      notifications.addNotification({
        title: t('auth.changeTemporaryPassword.notification.change'),
        text: ``,
        type: 'success',
        time: 10000
      })
      void router.push({ name: 'Login' })
    } else {
      loading.value = false
      wrongData.value = 'Error'
    }
  })
}

onMounted(() => {
  if (store.emailForgot === '') {
    void router.push({ name: 'Login' })
  } else {
    const inputsPassword = document.querySelectorAll('.block-input')
    for (let x = 0; x < inputsPassword.length; x++) {
      ;(inputsPassword[x] as any).onpaste = function (e: any) {
        e.preventDefault()
        notifications.addNotification({
          title: t(
            'auth.changeTemporaryPassword.notification.actionNotAllowed'
          ),
          text: ``,
          type: 'error',
          time: 10000
        })
      }
      ;(inputsPassword[x] as any).oncopy = function (e: any) {
        e.preventDefault()
        notifications.addNotification({
          title: t(
            'auth.changeTemporaryPassword.notification.actionNotAllowed'
          ),
          text: ``,
          type: 'error',
          time: 10000
        })
      }
    }
  }
})

onBeforeUnmount(() => {
  store.emailForgot = ''
  store.pass = ''
})
</script>
<template>
  <h2
    class="text-3xl xl:text-4xl font-bold"
    v-html="t('auth.changeTemporaryPassword.title')" />
  <p
    class="py-3 leading-5 text-gray-500"
    v-html="t('auth.changeTemporaryPassword.description')" />
  <div
    v-if="wrongData"
    class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4"
    role="alert">
    <p class="text-sm font-bold">{{ wrongData }}</p>
  </div>
  <form class="grid" @submit.prevent="onSubmit">
    <div class="grid relative mb-3">
      <DTextField
        class-input="block-input"
        :label="t('auth.changeTemporaryPassword.form.newPassword')"
        :type="showNewPassword ? 'password' : 'text'"
        :error="errors.userNewPassword"
        :placeholder="
          t('auth.changeTemporaryPassword.form.placeholder.newPassword')
        "
        :required="true"
        v-model="userNewPassword" />
      <div class="absolute right-3 top-8 z-50">
        <EyeIcon
          class="text-gray-600"
          @click="showNewPassword = !showNewPassword"
          :class="{
            hidden: !showNewPassword,
            block: showNewPassword
          }" />
        <EyeOffIcon
          class="text-gray-600"
          @click="showNewPassword = !showNewPassword"
          :class="{
            block: !showNewPassword,
            hidden: showNewPassword
          }" />
      </div>
    </div>
    <div class="grid relative mb-3">
      <DTextField
        class-input="block-input"
        :label="t('auth.changeTemporaryPassword.form.confirmNewPassword')"
        :type="showConfirmNewPassword ? 'password' : 'text'"
        :error="errors.userConfirmNewPassword"
        :placeholder="
          t('auth.changeTemporaryPassword.form.placeholder.confirmNewPassword')
        "
        :required="true"
        v-model="userConfirmNewPassword" />
      <div class="absolute right-3 top-8 z-50">
        <EyeIcon
          class="text-gray-600"
          @click="showConfirmNewPassword = !showConfirmNewPassword"
          :class="{
            hidden: !showConfirmNewPassword,
            block: showConfirmNewPassword
          }" />
        <EyeOffIcon
          class="text-gray-600"
          @click="showConfirmNewPassword = !showConfirmNewPassword"
          :class="{
            block: !showConfirmNewPassword,
            hidden: showConfirmNewPassword
          }" />
      </div>
    </div>
    <DBtn
      class="font-semibold py-3 text-base"
      :loading="loading"
      :color="
        errors.userNewPassword == null &&
        errors.userConfirmNewPassword == null &&
        userNewPassword != '' &&
        userConfirmNewPassword != ''
          ? 'blue'
          : 'disabled'
      "
      :disabled="
        errors.userNewPassword == null &&
        errors.userConfirmNewPassword == null &&
        userNewPassword != '' &&
        userConfirmNewPassword != ''
          ? false
          : true
      ">
      {{ t('auth.changeTemporaryPassword.title') }}
    </DBtn>
    <div class="flex justify-between pt-4 pb-6">
      <p v-html="t('auth.changeTemporaryPassword.havePassword')" />
      <router-link
        :to="{ name: 'Login' }"
        class="text-blue-600 hover:text-blue-500 font-bold"
        v-html="t('auth.changeTemporaryPassword.logIn')" />
    </div>
  </form>
</template>
